import React from 'react'
import {
	Alert, Button, Empty, Table, Tag
} from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import i18next from 'i18next'
import { fetchHandoverProtocolList } from './handoverProtocolApi'
import { HandoverProtocolRow } from './types'
import { formatProtocolDate } from './handoverProtocolRows'
import { history } from '../../utils/history'

type Props = {
	userID: number
	userName: string | null
	onOpenProtocol: (id: number) => void
}

/**
 * Preberacie protokoly na osobné veci pracovníka priradeného k zákazke.
 *
 * Osobný sklad patrí profilu zamestnanca, nie zákazke — tu je len na dočítanie, aby manažér
 * na tablete videl, či má daný človek podpísané aj svoje vlastné vybavenie. Vytvoriť nový
 * osobný protokol sa dá na jeho profile, kam vedie odkaz.
 */
const PersonalProtocolsSection = ({ userID, userName, onOpenProtocol }: Props) => {
	const { t } = useTranslation('pages')

	const { data, isLoading } = useQuery({
		queryKey: ['personalHandoverProtocols', userID],
		queryFn: () => fetchHandoverProtocolList({ type: 'PERSONAL', userID }),
		staleTime: 30_000
	})

	const protocols: HandoverProtocolRow[] = data?.protocols ?? []
	const needsSignature = data?.summary?.needsSignature ?? false

	const columns: ColumnsType<HandoverProtocolRow> = [
		{
			title: t('pages:Dátum') as string,
			key: 'signedAt',
			render: (_, row) => formatProtocolDate(row.signedAt)
		},
		{
			title: t('pages:Zamestnanec') as string,
			dataIndex: 'recipientName',
			key: 'recipientName'
		},
		{
			title: t('pages:Počet položiek') as string,
			dataIndex: 'itemsCount',
			key: 'itemsCount'
		}
	]

	return (
		<>
			<div className={'flex justify-between items-center mb-4'}>
				<h3 className={'mb-0'}>
					{t('pages:Preberacie protokoly na osobné veci')}
					{userName ? ` — ${userName}` : ''}
				</h3>
				<Button onClick={() => history.push(`${i18next.t('paths:userDetail|path')}/${userID}`)}>
					{t('pages:Otvoriť profil zamestnanca')}
				</Button>
			</div>

			{!isLoading && needsSignature && (
				<Alert
					type={'warning'}
					showIcon
					style={{ marginBottom: 12 }}
					message={protocols.length === 0
						? t('pages:Zamestnanec má osobné veci, ale nemá na ne podpísaný žiadny preberací protokol')
						: t('pages:Osobné veci sa zmenili až po poslednom podpise — protokol nesedí s aktuálnym stavom')}
				/>
			)}

			{protocols.length === 0 && !isLoading ? (
				<Empty description={t('pages:Žiadne podpísané protokoly na osobné veci') as string} />
			) : (
				<Table
					size={'small'}
					rowKey={'id'}
					dataSource={protocols}
					loading={isLoading}
					pagination={false}
					columns={columns}
					onRow={(row) => ({
						onClick: () => onOpenProtocol(row.id),
						style: { cursor: 'pointer' }
					})}
				/>
			)}
			{!isLoading && !needsSignature && protocols.length > 0 && (
				<Tag color={'green'} style={{ marginTop: 8 }}>{t('pages:Podpis je aktuálny')}</Tag>
			)}
		</>
	)
}

export default PersonalProtocolsSection
